import { Injectable, OnDestroy, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { EvalueeAnswer, Interest, InterestQuestion, InterestResult, InterestScore, InterestAnswer } from '@career-scope/models';
import { EvalueeService } from './evaluee.service';
import { Subscription } from 'rxjs';
import { AssessmentService } from './assessment.service';
import { RatingOption } from '../shared/components/rating-options/rating-options.component';

@Injectable()
export abstract class BaseInterestService implements OnDestroy {
  protected evalueeService = inject(EvalueeService);
  protected assessmentService = inject(AssessmentService);
  private assessmentSubscription: Subscription;

  consumerDirectEvaluee = signal(false);
  interestQsLength = computed(() => this.interestQs().length > 140 ? 140 : 130);
  interestsLength = computed(() => this.interests().length);
  answerOptionsCount = computed(() => this.ratingConfigOptions().length);
  interestAnswers = signal<InterestAnswer[]>([]);

  constructor() {
    this.assessmentSubscription = this.assessmentService.assessment.subscribe(assessment => {
      if (assessment === null) {
        this.resetInterestInventoryAnswers();
      }
    });
  }

  abstract interests: WritableSignal<Interest[]>;
  abstract interestQs: WritableSignal<InterestQuestion[]>;
  abstract ratingConfigOptions: WritableSignal<RatingOption[]>;
  abstract topInterests: WritableSignal<(Interest & InterestScore)[]>;
  abstract INTEREST_INVENTORY_TASK_ID: number;

  completeInterestsHQ() {
    this.completeInterestQuestions();
    this.saveInterestAnswers();
  }

  updateQuestions(questions: InterestQuestion[]) {
    this.interestQs.set(questions);
  }

  setTopInterests(topInterestScores: InterestScore[]) {
    this.topInterests.set(topInterestScores.map(score => {
      const matchingInterest = this.interests().find(interest => interest.interestCategory === score.interestCategory) as Interest;
      return {
        ...score,
        ...matchingInterest,
      };
    }));
  }

  getInterestQuestions(): Signal<InterestQuestion[]> {
    return this.interestQs;
  }

  getInterestInventoryAnswers(): EvalueeAnswer[] {
    return this.interestAnswers().filter(answer => answer.answer !== null).map(answer => ({
      taskId: this.INTEREST_INVENTORY_TASK_ID,
      questionId: answer.questionId,
      answer: answer.answer as number,
      completedAt: answer.completedAt || new Date(Date.now()),
    }));
  }

  hasInterestInventoryProgress(): boolean {
    return this.interestAnswers().length > 0;
  }

  isInterestInventoryComplete(): boolean {
    const answers = this.interestAnswers();
    const questions = this.interestQs();
    return answers.length === questions.length && answers.every(a => a.answer !== null);
  }

  private resetInterestInventoryAnswers() {
    this.interestAnswers.set([]);
  }

  private completeInterestQuestions() {
    const questions = this.interestQs();
    const existingAnswers = this.interestAnswers();

    const newAnswers = questions.map(q => {
      const existing = existingAnswers.find(a => a.questionId === q.id);
      if (existing) return existing;

      return {
        taskId: this.INTEREST_INVENTORY_TASK_ID,
        questionId: q.id,
        answer: Math.floor(Math.random() * this.answerOptionsCount() + 1),
        timeToComplete: 0,
        completedAt: new Date(Date.now()),
        deletedAt: null
      };
    });

    this.interestAnswers.set(newAnswers);
  }

  resetInterestsHQ() {
    const assessment = this.assessmentService.assessment.value;

    if (!assessment) {
      return;
    }

    const evalueeAnswers = assessment.evalueeAnswers.filter(answer => answer.taskId !== this.INTEREST_INVENTORY_TASK_ID);

    // Reset occupationOverallFit as well when reimplemented
    this.assessmentService.saveAssessment({ evalueeAnswers, interestResults: [], interestsComplete: null, interestScores: [], topInterestScores: [], completedDate: null, lastModifiedDate: new Date(Date.now()) }, assessment.portalId, assessment.evalueeId, assessment.id);
    this.evalueeService.updateEvaluee({ interestComplete: null, lastActivity: 'Interests Reset', lastActivityDate: new Date(Date.now()), interestStatus: 'Interests Reset', status: 'in progress' });
    this.resetInterestInventoryAnswers();
  }

  saveInterestAnswers() {
    const assessment = this.assessmentService.assessment.value;

    if (!assessment) {
      return;
    }

    const filteredAnswers = assessment.evalueeAnswers.filter(answer => answer.taskId !== this.INTEREST_INVENTORY_TASK_ID);
    const interestAnswers = this.getInterestInventoryAnswers();
    const interestAnswersCompletedCount = interestAnswers.filter(answer => answer.answer !== null).length;

    const evalueeAnswers = [...filteredAnswers, ...interestAnswers];

    const interestCompletedPercent = Math.round(interestAnswersCompletedCount / this.getInterestQuestions()().length * 100);

    const interestsComplete = interestAnswersCompletedCount === this.getInterestQuestions()().length ? new Date(Date.now()) : null;
    const lastActivity = interestsComplete ? 'Interest Completed' : 'Interest ' + interestCompletedPercent + '% complete';
    const interestStatus = interestCompletedPercent + '%';
    const lastActivityDate = new Date(Date.now());
    const interestResults: InterestResult[] = [];
    const interestScores: InterestScore[] = [];
    const topInterestScores: InterestScore[] = [];
    // const occupationOverallFit: { code: string, value: number; }[] = [];

    this.assessmentService.saveAssessment({ evalueeAnswers, interestResults, interestsComplete, interestScores, topInterestScores }, assessment.portalId, assessment.evalueeId, assessment.id);

    this.evalueeService.updateEvaluee({ interestComplete: interestsComplete, lastActivity, lastActivityDate, interestStatus });
  }

  ngOnDestroy(): void {
    this.assessmentSubscription?.unsubscribe();
  }
} 